import { gql, useMutation } from '@apollo/client';
import React from 'react';
import { useDispatch } from 'react-redux';
import useAppSelector from '../../app/redux/useAppSelector';
import { MenuItem, MenuSeparator } from '../../components/Menu';
import OverflowMenu from '../../components/OverflowMenu';
import Toast from '../../components/Toast';
import type {
  DeactivateUserMutation,
  DeactivateUserMutationVariables,
  ReactivateUserMutation,
  ReactivateUserMutationVariables,
  TeammateUserMenu_UserFragment,
  UpdateUserTypeMutation,
  UpdateUserTypeMutationVariables,
} from '../../graphql/generated/graphqlTypes';
import { PlanTier, UserType } from '../../graphql/generated/graphqlTypes';
import useSeatLimits from '../../invitation/useSeatLimits';
import UIActions from '../../ui/UIActions';
import UserProfileSettingsModal from '../../user/settings/UserProfileSettingsModal';
import {
  ALL_USERS_QUERY,
  USE_ALL_USERS_USER_FRAGMENT,
} from '../../user/useAllUsers';
import userActionPermissionCheck, {
  DEACTIVATE,
  DEMOTE_TO_USER,
  DOWNGRADE_TO_GUEST,
  EDIT_PROFILE,
  PROMOTE_TO_ADMIN,
  RESTORE_DEACTIVATED,
  UPGRADE_TO_USER,
} from '../../user/userActionPermissionCheck';
import updateQueryList from '../../utils/graphql/updateQueryList';
import DeactivateConfirmationModal from './DeactivateConfirmationModal';

const UPDATE_USER_TYPE_MUTATION = gql`
  mutation updateUserType($id: ID!, $type: UserType!) {
    updateUser(id: $id, type: $type) {
      id
      type
    }
  }
`;

const REACTIVATE_USER_MUTATION = gql`
  mutation reactivateUser($id: ID!) {
    reactivateUser(id: $id) {
      id
      deactivatedAt
      ...useAllUsers_user
    }
  }
  ${USE_ALL_USERS_USER_FRAGMENT}
`;

const DEACTIVATE_USER_MUTATION = gql`
  mutation deactivateUser($id: ID!, $newOwnerId: ID!) {
    deactivateUser(id: $id, newOwnerId: $newOwnerId) {
      id
      deactivatedAt
    }
  }
`;

const sentenceType = (type?: UserType | null) => {
  if (type === UserType.Admin) {
    return 'an admin';
  }
  if (type === UserType.Guest) {
    return 'a guest';
  }
  return 'a standard user';
};

const TEAMMATE_USER_MENU_USER = gql`
  fragment TeammateUserMenu_user on User {
    id
    type
    name
    ...DeactivateConfirmationModal_user
    ...userActionPermissionCheck_user
  }
  ${DeactivateConfirmationModal.fragments.user}
  ${userActionPermissionCheck.fragments.user}
`;

interface TeammateUserMenuProps {
  user: TeammateUserMenu_UserFragment;
}

const TeammateUserMenu = ({ user }: TeammateUserMenuProps) => {
  const currentUser = useAppSelector((state) => state.session.user);
  const planTier = useAppSelector(
    (state) => state.session.organization.planTier,
  );
  const dispatch = useDispatch();
  const isCurrentUser = currentUser.id === user.id;
  const { isSeatLimitMaxed, isGuestLimitMaxed } = useSeatLimits();

  const [updateUserType] = useMutation<
    UpdateUserTypeMutation,
    UpdateUserTypeMutationVariables
  >(UPDATE_USER_TYPE_MUTATION);

  const [reactivateUser] = useMutation<
    ReactivateUserMutation,
    ReactivateUserMutationVariables
  >(REACTIVATE_USER_MUTATION);

  const [deactivateUser] = useMutation<
    DeactivateUserMutation,
    DeactivateUserMutationVariables
  >(DEACTIVATE_USER_MUTATION);

  const handleDeactivate = (showUndo = true) => {
    dispatch(
      UIActions.openModal(DeactivateConfirmationModal, {
        user,
        onConfirm: async (newOwner: { id: string; name: string }) => {
          await deactivateUser({
            variables: { id: user.id, newOwnerId: newOwner.id },
            update: updateQueryList(
              { query: ALL_USERS_QUERY },
              'session.organization.users',
              'deactivateUser',
              false,
            ),
          });

          dispatch(
            UIActions.pushToast((toastProps: { onDismiss: () => void }) => (
              <Toast
                level="success"
                message={`${user.name} has been deactivated and owned posts have been transferred to ${newOwner.name}.`}
                onDismiss={toastProps.onDismiss}
                onSecondaryClick={() => {
                  // eslint-disable-next-line @typescript-eslint/no-use-before-define
                  handleRestore(false);
                  toastProps.onDismiss();
                }}
                secondaryText={showUndo ? 'Reactivate' : undefined}
              />
            )),
          );
        },
      }),
    );
  };

  const handleRestore = async (showUndo = true) => {
    await reactivateUser({
      variables: { id: user.id },
      update: updateQueryList(
        { query: ALL_USERS_QUERY },
        'session.organization.users',
        'reactivateUser',
        true,
      ),
    });

    dispatch(
      UIActions.pushToast((toastProps: { onDismiss: () => void }) => (
        <Toast
          level="success"
          message={`${user.name} has been restored as ${sentenceType(
            user.type,
          )}.`}
          onDismiss={toastProps.onDismiss}
          onSecondaryClick={() => {
            handleDeactivate(false);
            toastProps.onDismiss();
          }}
          secondaryText={showUndo ? 'Undo' : undefined}
        />
      )),
    );
  };

  const handleUserTypeUpdate = async (
    oldType: UserType,
    newType: UserType,
    showUndo = true,
  ) => {
    await updateUserType({ variables: { id: user.id, type: newType } });

    dispatch(
      UIActions.pushToast((toastProps: { onDismiss: () => void }) => (
        <Toast
          level="success"
          message={`${user.name} is now ${sentenceType(newType)}.`}
          onDismiss={toastProps.onDismiss}
          onSecondaryClick={() => {
            handleUserTypeUpdate(newType, oldType, false);
            toastProps.onDismiss();
          }}
          secondaryText={showUndo ? 'Undo' : undefined}
        />
      )),
    );
  };

  const isRestorationDisabled =
    user.type === UserType.Guest ? isGuestLimitMaxed : isSeatLimitMaxed;

  const isGuestUpgradableToUser =
    planTier === PlanTier.Free || !isSeatLimitMaxed;

  const menuItems = (
    [
      [
        RESTORE_DEACTIVATED,
        <MenuItem
          onClick={() => handleRestore(true)}
          disabled={isRestorationDisabled}
          tooltip={
            isRestorationDisabled &&
            (planTier === PlanTier.Free
              ? 'Your team has reached the seat limit for our free plan. Please upgrade to a paid plan to add additional teammates.'
              : 'Seat limit reached. Please contact support to adjust.')
          }
        >
          Restore as {sentenceType(user.type)}
        </MenuItem>,
      ],
      [
        EDIT_PROFILE,
        <MenuItem
          onClick={() =>
            dispatch(
              UIActions.openModal(UserProfileSettingsModal, {
                id: user.id,
              }),
            )
          }
        >
          Edit profile
        </MenuItem>,
      ],
      [
        DEMOTE_TO_USER,
        <MenuItem
          disabled={isCurrentUser}
          tooltip={isCurrentUser && 'You cannot demote yourself'}
          onClick={() => handleUserTypeUpdate(UserType.Admin, UserType.User)}
        >
          Demote to standard user
        </MenuItem>,
      ],
      [
        PROMOTE_TO_ADMIN,
        <MenuItem
          onClick={() => handleUserTypeUpdate(UserType.User, UserType.Admin)}
        >
          Promote to admin
        </MenuItem>,
      ],
      [
        DOWNGRADE_TO_GUEST,
        <MenuItem
          onClick={() => handleUserTypeUpdate(UserType.User, UserType.Guest)}
        >
          Downgrade to guest
        </MenuItem>,
      ],
      [
        UPGRADE_TO_USER,
        <MenuItem
          onClick={() => handleUserTypeUpdate(UserType.Guest, UserType.User)}
          disabled={!isGuestUpgradableToUser}
          tooltip={
            !isGuestUpgradableToUser &&
            'Seat limit reached. Please contact support to adjust.'
          }
        >
          Upgrade to standard user
        </MenuItem>,
      ],
      [
        DEACTIVATE,
        <>
          <MenuSeparator />
          <MenuItem
            danger
            disabled={isCurrentUser}
            tooltip={isCurrentUser && 'You cannot deactivate yourself'}
            onClick={() => handleDeactivate(true)}
          >
            Deactivate
          </MenuItem>
        </>,
      ],
    ] as const
  )
    .filter(([action]) => userActionPermissionCheck(action, user, currentUser))
    .map(([action, item]) => (
      <React.Fragment key={action}>{item}</React.Fragment>
    ));

  return menuItems.length > 0 ? (
    <OverflowMenu iconSize={16} applyNegativeMargin={false}>
      {menuItems}
    </OverflowMenu>
  ) : null;
};

TeammateUserMenu.fragments = { user: TEAMMATE_USER_MENU_USER };

export default TeammateUserMenu;
